export type SearchableCommand = {
  title: string;
  description: string;
  keywords: string;
};

function normalize(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("es");
}

function startsWithWord(value: string, term: string): boolean {
  return value.split(/\s+/).some((word) => word.startsWith(term));
}

const connectorWords = new Set([
  "a", "al", "como", "con", "de", "del", "donde", "el", "en", "hacer", "la", "las", "los", "mi", "mis", "necesito", "para", "por", "puedo", "quiero", "un", "una", "unas", "unos", "ver", "y",
]);

function meaningfulTerms(query: string): string[] {
  const words = normalize(query).split(/[^a-z0-9]+/).filter(Boolean);
  const withoutConnectors = words.filter((word) => !connectorWords.has(word));
  return withoutConnectors.length ? withoutConnectors : words;
}

function scoreTerm(command: SearchableCommand, term: string): number | null {
  const title = normalize(command.title);
  const description = normalize(command.description);
  const keywords = normalize(command.keywords);

  if (title === term) return 0;
  if (title.startsWith(term)) return 10;
  if (startsWithWord(title, term)) return 20;
  if (title.includes(term)) return 30;
  if (description.startsWith(term)) return 40;
  if (startsWithWord(keywords, term)) return 50;
  if (description.includes(term)) return 60;
  if (keywords.includes(term)) return 70;
  return null;
}

function commandScore(command: SearchableCommand, phrase: string, terms: string[]): number | null {
  const phraseScore = scoreTerm(command, phrase);
  if (terms.length <= 1) return phraseScore;

  const termScores = terms.map((term) => scoreTerm(command, term));
  let combinedScore = 0;
  for (const score of termScores) {
    if (score === null) return null;
    combinedScore += score;
  }

  // Una frase literal sigue siendo la señal más fuerte. Cuando las palabras
  // llegan separadas o en otro orden, todas deben existir y se ponderan juntas.
  return phraseScore ?? 100 + combinedScore;
}

export function rankCommands<T extends SearchableCommand>(commands: T[], query: string): T[] {
  const phrase = normalize(query.trim());
  if (!phrase) return commands;
  const terms = meaningfulTerms(phrase);
  return commands
    .map((command, index) => ({ command, index, score: commandScore(command, phrase, terms) }))
    .filter((item): item is typeof item & { score: number } => item.score !== null)
    .sort((left, right) => left.score - right.score || left.index - right.index)
    .map((item) => item.command);
}
